import React, {useEffect, useState} from "react";
import {deleteDevice, fetchDevice, fetchDevices} from "../../../api/devices";
import {IDevice, IFetchDevicesDto} from "../../../common/interfaces/devices";
import {createToastNotification} from "../../../utilities/helpers";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {Box, IconButton, Stack, TextField, Typography, Button} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import DeviceModal from "../modal/DeviceModal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ConfirmationModal from "../../../components/ConfirmationModal";
import ClearIcon from "@mui/icons-material/Clear";

export const Devices = () => {
    const [search, setSearch] = useState<string>('');
    const [deviceData, setDeviceData] = useState({
        id: 0,
        name: "",
        description: "",
        status: "",
        ipAddress: ""
    })

    const [selectedDevice, setSelectedDevice] = useState<IDevice | null >(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmDeleteDialogIsOpened, setConfirmDeleteDialogIsOpened] = useState(false);
    const [devicesData, setDevicesData] = useState<IDevice[]>([]);
    const [totalDevices, setTotalDevices] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const paginationModel = {page: page, pageSize: perPage};


    const handleFetchDevices = async (searchParam?: string) => {
        const data: IFetchDevicesDto = {
            page: page,
            perPage: perPage,
        };

        if (searchParam !== undefined) {
            // @ts-ignore
            data.search = searchParam
        } else if (search.length > 0) {
            // @ts-ignore
            data.search = search
        }

        try {
            let response = await fetchDevices(data);
            setDevicesData(response.devices);
            setTotalDevices(response.totalItems);
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        }
    }

    const handleClose = () => {
        setModalOpen(false);
        setDeviceData({
            id: 0,
            name: "",
            description: "",
            status: "",
            ipAddress: ""
        });
    }
    const handleOpen = () => {
        setModalOpen(true);
    }

    // @ts-ignore
    const handleRowClick = async (params) => {
        try {
            const response = await fetchDevice(params.row.id);
            setSelectedDevice(response.device);
            setModalOpen(true);
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        }
    }

    const handleSave = () => {
        setModalOpen(false);
        handleFetchDevices();
    }

    // @ts-ignore
    const handleOpenDeleteDialog = (device: IDevice) => {
        setSelectedDevice(device);
        setConfirmDeleteDialogIsOpened(true);
    }

    const handleCloseDeleteDialog = () => {
        setSelectedDevice(null)
        setConfirmDeleteDialogIsOpened(false);
    }

    // @ts-ignore
    const handleConfirmDelete = async () => {
        if(!selectedDevice?.id) {
            setSelectedDevice(null)
            setConfirmDeleteDialogIsOpened(false);
            return;
        }
        try {
            await deleteDevice(selectedDevice.id);
            createToastNotification('success', 'Uređaj uspešno izbrisan');
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        } finally {
            setSelectedDevice(null)
            setConfirmDeleteDialogIsOpened(false);
            await handleFetchDevices();
        }
    }

    const columns: GridColDef[] = [
        {field: 'id', headerName: 'ID', width: 70,sortable: false},
        {field: 'name', headerName: 'Naziv', flex: 2,sortable: false},
        {field: 'description', headerName: 'Opis', type: 'string', flex: 3,sortable: false},
        {field: 'status', headerName: 'Status', type: 'string', flex: 2,sortable: false},
        {field: 'ipAddress', headerName: 'IP Adresa', type: 'string', flex: 2,sortable: false},
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <IconButton
                    color="error"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleOpenDeleteDialog(params.row);
                    }}
                >
                    <DeleteOutlineIcon color="error" />
                </IconButton>
            ),
        },
    ];

    useEffect(() => {
        handleFetchDevices()
    }, [page, perPage]);

    useEffect(() => {
        if (selectedDevice) {
            setDeviceData(selectedDevice);
        }
    }, [selectedDevice])

    // @ts-ignore
    // @ts-ignore
    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h4">Lista uređaja</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        handleOpen();
                    }}
                    startIcon={<AddIcon />}
                >
                    Kreiraj uređaj
                </Button>
            </Box>
            <br />
            <Box width="100%">
                <Stack direction="row" alignItems="center">
                    <TextField
                        sx={{
                            width: '500px',
                            height: '50px',
                            marginRight: '20px',
                            '& .MuiInputBase-root': {
                                backgroundColor: 'white',
                                borderRadius: '5px',
                                padding: 0,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#d0d0d0',
                            },
                        }}
                        label="Pretraga po IP adresi ili nazivu"
                        name="search"
                        size="small"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFetchDevices(search);
                            }
                        }}
                        margin="normal"
                    />
                    <Tooltip title="Pretraži">
                        <IconButton
                            onClick={()=>{
                                handleFetchDevices()
                            }}
                            color="primary"
                            size="medium"
                            sx={{
                                backgroundColor: '#f0f0f0',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    {search.length>0 &&
                        <Tooltip title="Očisti unos">
                            <IconButton
                                onClick={()=>{
                                    setSearch('')
                                    handleFetchDevices('')
                                }}
                                color="error"
                                size="medium"
                                sx={{
                                    marginLeft:1,
                                    backgroundColor: '#f0f0f0',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0',
                                    },
                                }}
                            >
                                <ClearIcon />
                            </IconButton>
                        </Tooltip>}
                </Stack>
                <DataGrid
                    disableColumnMenu
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    rows={devicesData}
                    rowCount={totalDevices}
                    columns={columns}
                    onRowClick={handleRowClick}
                    pagination
                    paginationModel={{
                        page: paginationModel.page - 1,
                        pageSize: paginationModel.pageSize
                    }}
                    paginationMode={'server'}
                    pageSizeOptions={[5, 10]}
                    //@ts-ignore
                    onPaginationModelChange={(model) => {
                        setPage(model.page + 1)
                        setPerPage(model.pageSize)
                    }}
                    sx={{
                        marginTop:2,
                        paddingLeft:'10px',
                        paddingRight:'10px',
                        border: 0,
                        '.MuiDataGrid-cell:focus': {
                            outline: 'none',
                        }
                    }}
                    localeText={{
                        MuiTablePagination: {
                            labelRowsPerPage: "Redova po strani",
                            labelDisplayedRows: ({from, to, count}) =>
                                `${from} - ${to} od ${count !== -1 ? count : ` ${to}`}`,
                        },
                        noRowsLabel: "Nema dostupnih podataka",
                    }}
                />
                <DeviceModal
                    open={modalOpen}
                    onClose={()=> {
                        handleClose()
                        handleFetchDevices('')
                    }}
                    onSave={handleSave}
                    deviceData={deviceData}
                    setDeviceData={setDeviceData}
                />
                {confirmDeleteDialogIsOpened && (
                    <ConfirmationModal
                        confirmationModalTitle="Brisanje uređaja"
                        cancelAction={() => {
                            handleCloseDeleteDialog()
                        }}
                        handleAction={()=> {
                            handleConfirmDelete()
                        }}
                        confirmBtnTitle="Potvrdi brisanje"
                        modalDescription="Da li ste sigurni da želite da obrišete uređaj?"
                        cancelBtnTitle="Odustani"
                    />
                )}
            </Box>
        </Box>
    );
}
